import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Pagination() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📑 Advanced CSS: Pagination Styles</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Pagination is a UI pattern that helps users navigate through content
        spread across multiple pages. With CSS you can style pagination using
        <code> flexbox</code>, <code>gradients</code>,{" "}
        <code>hover effects</code>, and <code>animations</code> to create
        professional UI components.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>display:flex</code> for alignment and spacing of page
            links.
          </li>
          <li>
            <code>border-radius</code> creates rounded buttons or pills.
          </li>
          <li>
            <code>:hover</code> and <code>:active</code> improve interactivity.
          </li>
          <li>
            Gradients and transitions give modern, eye-catching pagination.
          </li>
          <li>
            Always include <code>.active</code> and <code>.disabled</code>{" "}
            states for usability.
          </li>
        </ul>
      </div>

      {/* Pagination Demo */}
      <div style={{ marginTop: "1rem", display: "flex", gap: "8px" }}>
        <span
          style={{
            padding: "6px 12px",
            border: "1px solid #3b82f6",
            borderRadius: "6px",
            background: "#3b82f6",
            color: "white",
          }}
        >
          1
        </span>
        <span
          style={{
            padding: "6px 12px",
            border: "1px solid #3b82f6",
            borderRadius: "6px",
            color: "#3b82f6",
          }}
        >
          2
        </span>
        <span
          style={{
            padding: "6px 12px",
            border: "1px solid #3b82f6",
            borderRadius: "6px",
            color: "#3b82f6",
          }}
        >
          3
        </span>
        <span
          style={{
            padding: "6px 12px",
            border: "1px solid #3b82f6",
            borderRadius: "6px",
            color: "#3b82f6",
          }}
        >
          {">"}
        </span>
      </div>

      {/* SECTION 1: Gradient Pagination Styles */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 Gradient Pagination Styles</h2>

        {/* Example 1 */}
        <h3>1. Blue to Purple Gradient</h3>
        <CodeCopyBlock
          code={`.pagination {
  display: flex;
  gap: 10px;
}
.pagination a {
  padding: 8px 14px;
  border-radius: 6px;
  color: white;
  background: linear-gradient(90deg, #3b82f6, #9333ea);
  text-decoration: none;
}
.pagination a.active {
  background: linear-gradient(90deg, #9333ea, #3b82f6);
}`}
        />
        <div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(90deg,#3b82f6,#9333ea)",
            }}
          >
            1
          </a>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(90deg,#9333ea,#3b82f6)",
            }}
          >
            2
          </a>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(90deg,#3b82f6,#9333ea)",
            }}
          >
            3
          </a>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(90deg,#9333ea,#3b82f6)",
            }}
          >
            {">"}
          </a>
        </div>

        {/* Example 2 */}
        <h3>2. Sunset Gradient</h3>
        <CodeCopyBlock
          code={`.pagination a {
  background: linear-gradient(45deg,#f43f5e,#f97316);
  color: white;
}`}
        />
        <div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(45deg,#f43f5e,#f97316)",
            }}
          >
            1
          </a>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(45deg,#f97316,#f43f5e)",
            }}
          >
            2
          </a>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(45deg,#f43f5e,#f97316)",
            }}
          >
            3
          </a>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(45deg,#f97316,#f43f5e)",
            }}
          >
            {">"}
          </a>
        </div>

        {/* Example 3 */}
        <h3>3. Green Aqua Gradient</h3>
        <CodeCopyBlock
          code={`.pagination a {
  background: linear-gradient(90deg,#10b981,#06b6d4);
  color: white;
}`}
        />
        <div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(90deg,#10b981,#06b6d4)",
            }}
          >
            1
          </a>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(90deg,#06b6d4,#10b981)",
            }}
          >
            2
          </a>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(90deg,#10b981,#06b6d4)",
            }}
          >
            3
          </a>
          <a
            href="#"
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              color: "white",
              background: "linear-gradient(90deg,#06b6d4,#10b981)",
            }}
          >
            {">"}
          </a>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Try **glassmorphism pagination** with blur effects.</li>
          <li>Add **icon arrows** (◀ ▶) for previous/next navigation.</li>
          <li>Make **responsive pagination** with flex-wrap.</li>
          <li>Use **CSS variables** for easy theme customization.</li>
          <li>Combine **animations + gradients** for futuristic designs.</li>
        </ul>
      </div>
    </div>
  );
}
